import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Calendar,
  Circle,
  Code,
  CornerDownRight,
  FileText,
  Lightbulb,
  Phone,
  Puzzle,
  Tag,
  User,
  WandSparkles,
} from 'lucide-react'
import { LoadingSpinner } from '@/components/common'
import { usePermissions } from '@/hooks/api'
import { useIsMobile } from '@/hooks/use-breakpoint'
import { useAssistantsByIds } from '@/hooks/api/useAssistants'
import { useCustomersSimple } from '@/hooks/api/useCustomers'
import { BACKEND_VERSION_FILTER_PRESETS, getBackendVersionKind } from '@/lib/backendVersion'
import { formatDate, formatDateInTimezone, parseUTCTimestamp } from '@/lib/dateFormat'
import { cn } from '@/lib/utils'
import { BackendVersionBadge } from '../components/BackendVersionBadge'
import { formatDurationCompact } from '../components/DurationDisplay'
import { QALabelBadge } from '../components/QALabelBadge'
import { InboxActiveFilters, type InboxActiveFilter } from './InboxActiveFilters'
import { InboxAssistantOptions } from './InboxAssistantOptions'
import { InboxChannelIcons } from './InboxChannelIcons'
import { InboxDateRangeFilter } from './InboxDateRangeFilter'
import { InboxFilterMenu, type InboxFilterCategory } from './InboxFilterMenu'
import { InboxFilterOptions } from './InboxFilterOptions'
import { InboxMobileFilters } from './InboxMobileFilters'
import { InboxOutcomeBadge } from './InboxOutcomeBadge'
import { InboxTextFilterInput } from './InboxTextFilterInput'
import { BOOKING_SYSTEMS, INBOX_OUTCOMES, type InboxFilters } from './inboxFilters'
import type { InboxFilterSection } from './inboxFilterSection'
import { getInboxTitle } from './inboxTitle'
import type { Assistant } from '@/types/assistant'
import { adminTags, basicTags, type Conversation } from '@/types/conversation'

interface InboxListProps {
  conversations: Conversation[]
  assistants: Assistant[]
  filters: InboxFilters
  onFiltersChange: (filters: InboxFilters) => void
  onClearFilters: () => void
  selectedId?: string
  viewedIds: Set<string>
  onSelect: (conversation: Conversation) => void
  isLoading: boolean
  /** The rows on screen belong to the previous filter while the next page is on its way */
  isUpdating?: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
  className?: string
}

function formatListDay(date: Date, timezone?: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value
  return `${part('day')}.${part('month')}.${part('year')}`
}

function formatListTime(date: string, yesterdayLabel: string, timezone?: string): string {
  const value = parseUTCTimestamp(date)
  const day = formatListDay(value, timezone)
  if (day === formatListDay(new Date(), timezone)) {
    return formatDateInTimezone(value, timezone, {
      year: undefined,
      month: undefined,
      day: undefined,
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  if (day === formatListDay(new Date(Date.now() - 24 * 60 * 60 * 1000), timezone)) {
    return yesterdayLabel
  }
  return day
}

function formatFilterDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number)
  return formatDate(new Date(year, month - 1, day), 'P')
}

function toFilterCategory(section: InboxFilterSection): InboxFilterCategory {
  const base = {
    id: section.id,
    label: section.label,
    icon: section.icon,
    activeCount: section.activeCount,
  }

  if (section.kind === 'assistants') {
    return {
      ...base,
      content: <InboxAssistantOptions selected={section.selected} onChange={section.onChange} />,
    }
  }

  if (section.kind === 'options') {
    return {
      ...base,
      content: (
        <InboxFilterOptions
          options={section.options}
          selected={section.selected}
          onChange={section.onChange}
          selection={section.selection}
          allowsCustomValue={section.allowsCustomValue}
        />
      ),
    }
  }

  if (section.kind === 'text') {
    return {
      ...base,
      content: (
        <InboxTextFilterInput
          placeholder={section.placeholder}
          value={section.value}
          onChange={section.onChange}
          inputMode={section.inputMode}
        />
      ),
    }
  }

  return {
    ...base,
    content: (
      <InboxDateRangeFilter from={section.from} to={section.to} onChange={section.onChange} />
    ),
  }
}

export function InboxList({
  conversations,
  assistants,
  filters,
  onFiltersChange,
  onClearFilters,
  selectedId,
  viewedIds,
  onSelect,
  isLoading,
  isUpdating = false,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  className,
}: InboxListProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { can, isAdmin } = usePermissions()
  const canFilterCustomers = can('view:customers')
  const { data: customers = [] } = useCustomersSimple(canFilterCustomers)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)
  const scrolledForId = useRef<string | undefined>(undefined)

  // A direct link sets the selection before its row exists. Scroll once that row
  // is rendered, and again only when the selection itself moves.
  useEffect(() => {
    if (!selectedId || scrolledForId.current === selectedId || !selectedRef.current) return
    selectedRef.current.scrollIntoView?.({ block: 'nearest' })
    scrolledForId.current = selectedId
  }, [selectedId, conversations])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage || isUpdating) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) onLoadMore()
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, isUpdating, onLoadMore])

  const { data: selectedAssistantRows = [] } = useAssistantsByIds(filters.assistant_ids)
  const assistantById = new Map(assistants.map((assistant) => [assistant.id, assistant]))
  const selectedAssistantName = new Map(
    selectedAssistantRows.map((assistant) => [assistant.id, assistant.name])
  )
  const customerById = new Map(customers.map((customer) => [customer.id, customer.name]))
  const tagById = canFilterCustomers ? { ...basicTags, ...adminTags } : basicTags
  const bookingSystemById = new Map(
    BOOKING_SYSTEMS.map((bookingSystem) => [bookingSystem.value, bookingSystem.label])
  )
  const backendVersionById = new Map<string, string>([
    [BACKEND_VERSION_FILTER_PRESETS.rc, t('resources.conversations.filters.backend_version_rc')],
    [
      BACKEND_VERSION_FILTER_PRESETS.preview,
      t('resources.conversations.filters.backend_version_preview'),
    ],
    [
      BACKEND_VERSION_FILTER_PRESETS.smoke,
      t('resources.conversations.filters.backend_version_smoke'),
    ],
  ])

  const activeFilters: InboxActiveFilter[] = [
    ...filters.assistant_ids.map((id) => ({
      id: `assistant-${id}`,
      label: selectedAssistantName.get(id) ?? assistantById.get(id)?.name ?? String(id),
      onRemove: () =>
        onFiltersChange({
          ...filters,
          assistant_ids: filters.assistant_ids.filter((value) => value !== id),
        }),
    })),
    ...filters.customer_ids.map((id) => ({
      id: `customer-${id}`,
      label: customerById.get(id) ?? String(id),
      onRemove: () =>
        onFiltersChange({
          ...filters,
          customer_ids: filters.customer_ids.filter((value) => value !== id),
        }),
    })),
    ...(filters.channels.length === 1
      ? [
          {
            id: `channel-${filters.channels[0]}`,
            label: t(
              filters.channels[0] === 'phone'
                ? 'resources.conversations.filters.channel_phone'
                : 'resources.conversations.filters.channel_whatsapp'
            ),
            onRemove: () => onFiltersChange({ ...filters, channels: [] }),
          },
        ]
      : []),
    ...(filters.phone_number.trim()
      ? [
          {
            id: 'phone-number',
            label: filters.phone_number.trim(),
            onRemove: () => onFiltersChange({ ...filters, phone_number: '' }),
          },
        ]
      : []),
    ...(filters.topic.trim()
      ? [
          {
            id: 'topic',
            label: filters.topic.trim(),
            onRemove: () => onFiltersChange({ ...filters, topic: '' }),
          },
        ]
      : []),
    ...filters.outcomes.map((outcome) => ({
      id: `outcome-${outcome}`,
      label: t(`resources.conversations.outcomes.${outcome}`),
      onRemove: () =>
        onFiltersChange({
          ...filters,
          outcomes: filters.outcomes.filter((value) => value !== outcome),
        }),
    })),
    ...filters.booking_systems.map((bookingSystem) => ({
      id: `booking-system-${bookingSystem}`,
      label: bookingSystemById.get(bookingSystem) ?? bookingSystem,
      onRemove: () =>
        onFiltersChange({
          ...filters,
          booking_systems: filters.booking_systems.filter((value) => value !== bookingSystem),
        }),
    })),
    ...(filters.backend_versions.length === 1
      ? [
          {
            id: `backend-version-${filters.backend_versions[0]}`,
            label:
              backendVersionById.get(filters.backend_versions[0]) ?? filters.backend_versions[0],
            onRemove: () => onFiltersChange({ ...filters, backend_versions: [] }),
          },
        ]
      : []),
    ...filters.tags.map((tag) => ({
      id: `tag-${tag}`,
      label: tagById[tag as keyof typeof tagById] ?? tag,
      onRemove: () =>
        onFiltersChange({ ...filters, tags: filters.tags.filter((value) => value !== tag) }),
    })),
    ...(filters.summary.trim()
      ? [
          {
            id: 'summary',
            label: filters.summary.trim(),
            onRemove: () => onFiltersChange({ ...filters, summary: '' }),
          },
        ]
      : []),
    ...(filters.date_from || filters.date_to
      ? [
          {
            id: 'date',
            label: [filters.date_from, filters.date_to]
              .filter(Boolean)
              .map(formatFilterDate)
              .join('–'),
            onRemove: () => onFiltersChange({ ...filters, date_from: '', date_to: '' }),
            keepVisible: true,
          },
        ]
      : []),
  ]

  const sectionsFor = (
    value: InboxFilters,
    update: (next: InboxFilters) => void
  ): InboxFilterSection[] => [
    {
      kind: 'assistants',
      id: 'assistant',
      label: t('resources.conversations.inbox.filters.assistant'),
      icon: WandSparkles,
      activeCount: value.assistant_ids.length,
      selected: value.assistant_ids,
      onChange: (assistant_ids) => update({ ...value, assistant_ids }),
    },
    ...(canFilterCustomers
      ? [
          {
            kind: 'options' as const,
            id: 'customer',
            label: t('resources.conversations.filters.customer'),
            icon: User,
            activeCount: value.customer_ids.length,
            options: customers.map((customer) => ({
              value: customer.id,
              label: customer.name,
            })),
            selected: value.customer_ids,
            onChange: (customer_ids: Array<string | number>) =>
              update({ ...value, customer_ids: customer_ids as InboxFilters['customer_ids'] }),
          },
        ]
      : []),
    {
      kind: 'options',
      id: 'channel',
      label: t('resources.conversations.inbox.filters.channel'),
      icon: Circle,
      activeCount: value.channels.length === 1 ? 1 : 0,
      selection: 'single',
      options: [
        { value: '', label: t('resources.conversations.filters.channel_all') },
        { value: 'phone', label: t('resources.conversations.filters.channel_phone') },
        { value: 'whatsapp', label: t('resources.conversations.filters.channel_whatsapp') },
      ],
      selected: value.channels.length === 1 ? value.channels : [''],
      onChange: (channels) =>
        update({
          ...value,
          channels: channels.filter(
            (channel): channel is InboxFilters['channels'][number] =>
              channel === 'phone' || channel === 'whatsapp'
          ),
        }),
    },
    {
      kind: 'text',
      id: 'phone_number',
      label: t('resources.conversations.filters.phone_number'),
      icon: Phone,
      activeCount: value.phone_number.trim() ? 1 : 0,
      placeholder: t('resources.conversations.filters.search_phone'),
      value: value.phone_number,
      inputMode: 'tel',
      onChange: (phone_number) => update({ ...value, phone_number }),
    },
    {
      kind: 'text',
      id: 'topic',
      label: t('resources.conversations.filters.topic'),
      icon: Lightbulb,
      activeCount: value.topic.trim() ? 1 : 0,
      placeholder: t('resources.conversations.filters.search_topic'),
      value: value.topic,
      onChange: (topic) => update({ ...value, topic }),
    },
    {
      kind: 'options',
      id: 'outcome',
      label: t('resources.conversations.inbox.filters.outcome'),
      icon: CornerDownRight,
      activeCount: value.outcomes.length,
      options: INBOX_OUTCOMES.map((outcome) => ({
        value: outcome,
        label: t(`resources.conversations.outcomes.${outcome}`),
      })),
      selected: value.outcomes,
      onChange: (outcomes) =>
        update({
          ...value,
          outcomes: outcomes.filter((outcome): outcome is InboxFilters['outcomes'][number] =>
            INBOX_OUTCOMES.includes(outcome as InboxFilters['outcomes'][number])
          ),
        }),
    },
    {
      kind: 'date',
      id: 'date',
      label: t('resources.conversations.fields.date'),
      icon: Calendar,
      activeCount: value.date_from || value.date_to ? 1 : 0,
      from: value.date_from,
      to: value.date_to,
      onChange: (date_from, date_to) => update({ ...value, date_from, date_to }),
    },
    {
      kind: 'options',
      id: 'tags',
      label: t('resources.conversations.filters.tags'),
      icon: Tag,
      activeCount: value.tags.length,
      options: Object.entries(tagById).map(([optionValue, label]) => ({
        value: optionValue,
        label,
      })),
      selected: value.tags,
      onChange: (tags) => update({ ...value, tags: tags as InboxFilters['tags'] }),
    },
    {
      kind: 'text',
      id: 'summary',
      label: t('resources.conversations.filters.summary'),
      icon: FileText,
      activeCount: value.summary.trim() ? 1 : 0,
      placeholder: t('resources.conversations.filters.search_summary'),
      value: value.summary,
      onChange: (summary) => update({ ...value, summary }),
    },
    ...(isAdmin
      ? [
          {
            kind: 'options' as const,
            id: 'booking_system',
            label: t('resources.conversations.filters.booking_system'),
            icon: Puzzle,
            activeCount: value.booking_systems.length,
            options: BOOKING_SYSTEMS,
            selected: value.booking_systems,
            onChange: (booking_systems: Array<string | number>) =>
              update({
                ...value,
                booking_systems: booking_systems as InboxFilters['booking_systems'],
              }),
          },
          {
            kind: 'options' as const,
            id: 'backend_version',
            label: t('resources.conversations.filters.backend_version'),
            icon: Code,
            activeCount: value.backend_versions.length === 1 ? 1 : 0,
            selection: 'single',
            options: Array.from(backendVersionById, ([optionValue, label]) => ({
              value: optionValue,
              label,
            })),
            selected: value.backend_versions.length === 1 ? value.backend_versions : [],
            onChange: (backend_versions: Array<string | number>) =>
              update({
                ...value,
                backend_versions: backend_versions
                  .filter((version): version is string => typeof version === 'string')
                  .slice(0, 1),
              }),
            allowsCustomValue: true,
          },
        ]
      : []),
  ]

  return (
    <section
      className={cn(
        'flex min-h-0 min-w-0 flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900',
        className
      )}
    >
      <div className="shrink-0 border-b border-gray-200 dark:border-gray-800">
        <div className="flex h-15 items-center gap-2 px-4">
          <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.conversations.inbox.title')}
          </h2>
          {isMobile ? (
            <InboxMobileFilters
              filters={filters}
              onApply={onFiltersChange}
              sectionsFor={sectionsFor}
            />
          ) : (
            <InboxFilterMenu
              categories={sectionsFor(filters, onFiltersChange).map(toFilterCategory)}
            />
          )}
        </div>
        <InboxActiveFilters filters={activeFilters} onClearAll={onClearFilters} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-2 pb-4">
        {isLoading || (isUpdating && conversations.length === 0) ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {t('resources.conversations.inbox.no_conversations')}
            </span>
            <span className="text-xs text-gray-400">
              {t('resources.conversations.inbox.no_conversations_hint')}
            </span>
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {isUpdating && (
              <li className="flex justify-center py-2">
                <LoadingSpinner size="sm" />
              </li>
            )}
            {conversations.map((conversation) => {
              const assistant = conversation.assistant_id
                ? assistantById.get(conversation.assistant_id)
                : undefined
              const isSelected = String(conversation.id) === selectedId
              const isViewed = viewedIds.has(String(conversation.id))
              // Calls handled by a non-release backend (RC, PR preview, smoke build)
              const versionKind = isAdmin
                ? getBackendVersionKind(conversation.backend_api_version)
                : null

              return (
                <li key={conversation.id}>
                  <button
                    type="button"
                    ref={isSelected ? selectedRef : undefined}
                    onClick={() => onSelect(conversation)}
                    aria-current={isSelected ? 'true' : undefined}
                    className={cn(
                      'flex w-full cursor-pointer flex-col gap-1 rounded-lg px-3 py-4 text-left',
                      isSelected
                        ? 'bg-gray-100 dark:bg-gray-800'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/60',
                      isViewed && !isSelected && 'opacity-70',
                      versionKind === 'rc' && 'border-l-4 border-l-amber-500',
                      versionKind === 'preview' && 'border-l-4 border-l-purple-500',
                      versionKind === 'smoke' && 'border-l-4 border-l-blue-500'
                    )}
                  >
                    <span className="flex w-full items-center gap-1">
                      <InboxChannelIcons conversation={conversation} />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 first-letter:uppercase dark:text-gray-100">
                        {getInboxTitle(conversation, t)}
                      </span>
                      <span className="text-xs whitespace-nowrap text-gray-400 dark:text-gray-500">
                        {formatListTime(
                          conversation.updated_at,
                          t('resources.conversations.inbox.yesterday'),
                          assistant?.timezone
                        )}
                      </span>
                    </span>
                    <span className="flex w-full items-center gap-1.5 text-xs whitespace-nowrap text-gray-600 dark:text-gray-400">
                      <span className="truncate">
                        {conversation.from_id || t('resources.conversations.unknown')}
                      </span>
                      {!!conversation.duration && (
                        <>
                          <span
                            aria-hidden="true"
                            className="size-1.5 shrink-0 rounded-full bg-current"
                          />
                          <span className="tabular-nums">
                            {formatDurationCompact(conversation.duration)}
                          </span>
                        </>
                      )}
                      {assistant && (
                        <>
                          <span
                            aria-hidden="true"
                            className="size-1.5 shrink-0 rounded-full bg-current"
                          />
                          <span className="min-w-0 truncate">{assistant.name}</span>
                        </>
                      )}
                    </span>
                    {conversation.summary?.trim() && (
                      <span className="line-clamp-2 text-sm leading-5 text-gray-600 dark:text-gray-400">
                        {conversation.summary}
                      </span>
                    )}
                    {(conversation.outcome || isAdmin) && (
                      <span className="flex flex-wrap items-center gap-1.5 empty:hidden">
                        {conversation.outcome && (
                          <InboxOutcomeBadge outcome={conversation.outcome} />
                        )}
                        {isAdmin && <QALabelBadge meta={conversation.meta} />}
                        {versionKind && (
                          <BackendVersionBadge version={conversation.backend_api_version} />
                        )}
                      </span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        <div ref={sentinelRef} />
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>
    </section>
  )
}
